/**
 * ── MILESTONE 2 · BLOCKS IN THE LEFT PANEL, PHASE 1 (Jack | Layne 1:1, 2026-10-06) ──────
 *
 * Layne's two-phase plan for the building-blocks milestone:
 *   1. VALIDATE THE BUILD — take every guardrail off, let any block go anywhere, and
 *      confirm the blocks still render correctly when reordered.
 *   2. DEFINE THE GUARDRAILS — a group decision on which blocks appear in the left
 *      panel and where each may sit. Can keep improving after launch.
 *
 * This module is phase 1's switchboard. It is PURE (no React) so every rule here is
 * unit-tested off the real block fixture, and the Studio only wires it.
 *
 * WHERE IT IS ON: never in production unless the environment forces it. On a non-prod
 * host (dev / qa / uat / localhost / file://) it turns on with `?m2=1`, so the test bed
 * is a link Jack can share without a separate deploy. `features.m2Blocks` in config.js
 * (FEATURE_M2_BLOCKS on the pod) overrides either way.
 *
 * WHAT "GUARDRAILS OFF" MEANS, exactly — nothing more:
 *   • placement rules (pins, mustImmediatelyFollow/Precede, movesWith, brackets) do not
 *     refuse a move or an insert;
 *   • count rules (maxCount, maxCombinedCount, mutuallyExclusive) do not refuse an add
 *     or a duplicate.
 * Field caps, required fields and the data gate are NOT touched — those govern copy, not
 * layout, and phase 1 is a layout test.
 */
import { nonProdHost } from "./namedDrafts";

/* `window.__APP_CONFIG__` is declared once, in authConfig.ts (AppConfig carries `features`) */
function runtimeFlag(): boolean | undefined {
  const cfg = typeof window !== "undefined" ? window.__APP_CONFIG__ : undefined;
  const v = cfg && cfg.features ? cfg.features.m2Blocks : undefined;
  return typeof v === "boolean" ? v : undefined;
}

/** Is the Milestone 2 block palette on for this page? */
export function m2BlocksEnabled(loc?: { hostname: string; protocol: string; search: string }): boolean {
  const o = runtimeFlag();
  if (typeof o === "boolean") return o;
  if (process.env.REACT_APP_FEATURE_M2_BLOCKS === "true") return true;
  try {
    const l = loc || (typeof window !== "undefined" ? window.location : undefined);
    if (!l || !nonProdHost(l)) return false;
    return /(?:^|[?&])m2=1(?:&|$)/.test(l.search || "");
  } catch {
    return false;
  }
}

/* ── THE PALETTE ──────────────────────────────────────────────────────────────────── */

/** one chip in the left panel */
export interface PalItem {
  /** the block type (`sfmc_<contentId>`) — what `makeBlock` takes */
  type: string;
  /** the SFMC block name, e.g. `ECH_CopyBlock` */
  name: string;
  /** what the chip says */
  label: string;
  /** the block declares no editable field (banner art, disclaimers, spacers) */
  locked: boolean;
  /** the family this chip stands for (see M2_FAMILIES), absent for a one-block family */
  family?: string;
  /** every member's SFMC name, so the Template tab can find the chip from any of them */
  members?: string[];
  /** phase 2: why this chip can't be added right now (a cap or "never both" rule) — greyed */
  off?: string;
}
export interface PalGroup { id: string; title: string; items: PalItem[] }

/** the minimal block shape the palette reads — EchMeta satisfies it */
export interface PalMeta {
  key: string;
  echName: string;
  locked: boolean;
  gr?: { label?: string } | null;
  /** `panelGroup` — the left-panel group the block asks for (Layne 10-29) */
  full?: { label?: string; panelGroup?: string } | null;
}

/**
 * Groups, in the order a reader builds an email top to bottom. Matched on the SFMC
 * NAME, first match wins, so a block Layne adds lands in a group with no code change
 * when its name follows her convention — and in "Other" when it does not, never lost.
 */
const GROUPS: { id: string; title: string; re: RegExp }[] = [
  { id: "top", title: "Header & hero", re: /^ECH_(Logo|MainImage|SecondaryImage|CommunitySubHead)|Image$/i },
  { id: "copy", title: "Copy & buttons", re: /^ECH_(CopyBlock|\dCTA|GOTY|GOInvite)/i },
  { id: "homes", title: "Homes & plans", re: /^ECH_(Featured|ListingTable|TableHeader)|Listing Table/i },
  { id: "community", title: "Community", re: /^ECH_(\d?Amenities|GetDirections)/i },
  { id: "space", title: "Spacing", re: /^ECH_(SectionSpacer|DividerLine)/i },
  { id: "footer", title: "Footer", re: /^ECH_(ContactUs|SocialIcons|Disclaimer)/i },
];
/* ── NOT A BLOCK A BUILDER ADDS (measured on the live library 2026-10-06, 47 → 30) ──
   The ECH_ prefix admits more than content blocks. Off the live pull, the panel would
   have offered: the document shell, Layne's numbered working copies, the blank
   template, fourteen IMAGE ASSETS that live in the same folder under ECH_ names
   (`ECH_ComingSoonImage_Light.png`, `…_Headline_DarkMode.png`), and a block whose own
   label says "RETIRED - do not use". None of these is in the bundled fixture, which is
   why the name rule and the label rule are both tested on literal live names. */
const NOT_A_BLOCK = /^ECH_Shell$|_Version[ _]?\d+$|_Template$|\.(png|jpe?g|gif|svg|webp)$/i;
const RETIRED_LABEL = /^\s*retired\b/i;

/** "ECH_GOInvite2CTA" → "GO Invite 2 CTA" when the block declares no label */
export function nameWords(name: string): string {
  return String(name || "")
    .replace(/^ECH_/i, "")
    .replace(/^\d+\s*-\s*/, "")
    .replace(/([a-z])([A-Z0-9])/g, "$1 $2")
    .replace(/(\d)([A-Za-z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
}

/* ── BLOCK FAMILIES (Jack, 2026-10-06: "we should leverage family blocks not individual
   blocks"; the 10-05 shell call: "from the app perspective, multiple block variants should
   still appear as one block with feature options — building a block family") ──────────
   One chip per family in the left panel; the variant is chosen in the right panel with
   the same strip the CTA row already uses ("One button | Two buttons", ECH_SWAP). The
   FIRST member is what a chip click places. A member Layne has not pulled is skipped, and
   a family with one member left is just that block. A block in no family is its own. */
export interface M2Family {
  id: string;
  /** what the chip says */
  label: string;
  /** [SFMC name, what its button in the right-panel strip says] — first is the default */
  members: [string, string][];
  /** fields whose KEYS differ between members: target name → { fromField: toField }.
   *  Same-key fields carry anyway (`carrySame`). */
  map?: Record<string, Record<string, string>>;
}
export const M2_FAMILIES: M2Family[] = [
  { id: "hero", label: "Hero Headline Image", members: [["ECH_ComingSoonImage", "Coming Soon"], ["ECH_AvailableNowImage", "Available Now"], ["ECH_FindDreamHomeImage", "Dream Home"], ["ECH_JustLaunchedImage", "Just Launched"], ["ECH_VIPImage", "VIP"],
    /* the three banners Layne added for the 10/6 templates (#360) — one Hero Headline Image
       chip, the look swapped in the right panel (Jack, 2026-10-12: "some of these feel like the
       same block just different visuals … make it one option but can swap") */
    ["ECH_WereOpenImage", "We're Open"], ["ECH_LastChanceImage", "Last Chance"], ["ECH_FloorplansImage", "Floorplans"]] },
  { id: "main", label: "Main Image", members: [["ECH_MainImage", "500 \u00d7 350"], ["ECH_MainImage600", "600 \u00d7 350"]] },
  { id: "cta", label: "CTA Buttons", members: [["ECH_1CTA", "One button"], ["ECH_2CTA", "Two buttons"]] },
  { id: "goinvite", label: "Grand Opening Event", members: [["ECH_GOInvite1CTA", "One button"], ["ECH_GOInvite2CTA", "Two buttons"]] },
  /* Layne 10-29: the two-button Thank You (ECH_GOTY2CTA) is the alternate in the Grand Opening
     Thank You template — same strip as the Invite. Headline / CommunityName / BodyCopy share
     their keys and carry; the one button becomes button 1 and back. */
  { id: "goty", label: "Grand Opening Thank You", members: [["ECH_GOTY1CTA", "One button"], ["ECH_GOTY2CTA", "Two buttons"]],
    map: { ECH_GOTY2CTA: { CtaLabel: "cta1Label", CtaUrl: "cta1Url" }, ECH_GOTY1CTA: { cta1Label: "CtaLabel", cta1Url: "CtaUrl" } } },
  { id: "featured", label: "Featured Plan", members: [["ECH_Featured1CTA", "One button"], ["ECH_Featured2CTA", "Two buttons"]] },
  { id: "spacer", label: "Section Spacer", members: [["ECH_SectionSpacer25", "25 px"], ["ECH_SectionSpacer50", "50 px"]] },
  { id: "disclaimer", label: "Disclaimer", members: [["ECH_Disclaimer", "Standard"], ["ECH_DisclaimerMortgage", "Mortgage"], ["ECH_DisclaimerMortgageTitle", "+ Title"], ["ECH_DisclaimerMortgageTitleLIA", "+ Title (LIA)"]] },
];
/* ── THE HERO LOOKS, GROUPED BY STAGE (Jack picked option 3, 2026-10-12) ─────────────
   The right panel shows the hero banners as pictures under the stage a community is in,
   so the stage points at the banner. The stages are the ones the templates already carry
   (Tease / Launch / Sustain / Last Chance — echTemplates `stage`); "Selling" is how
   Sustain reads to a division. A banner whose block declares `stage` in its guardrails
   uses that instead, so Layne can move one without a code change. */
export const M2_STAGES = ["Tease", "Launch", "Selling", "Last chance"] as const;
export const M2_HERO_STAGE: Record<string, string> = {
  ECH_ComingSoonImage: "Tease", ECH_VIPImage: "Tease", ECH_FloorplansImage: "Tease",
  ECH_JustLaunchedImage: "Launch", ECH_WereOpenImage: "Launch",
  ECH_AvailableNowImage: "Selling", ECH_FindDreamHomeImage: "Selling",
  ECH_LastChanceImage: "Last chance",
};
/* ── KATY'S ADD-BLOCK RULES (Teams 2026-10-16) ─────────────────────────────────────────
   "Each module can only appear once per email. … They can add an Event or Map module if the
   template doesn't already have one. … Requesters can't add buttons. Templates stay at the
   buttons they ship with." And: "confirm the app enforces these limits, so the option to add
   a duplicate module or an extra button never shows up."
   Held in the app, not in each block's `maxCount`, so it holds on every template — including
   a _TEST block that has not been given a cap yet, and the guardrails-off test bed.
   Exempt: a Featured run (1–5 cards is ONE module, Layne 10-15), and the furniture that is
   not a module at all — spacers, dividers, the section bars. */
const M2_BUTTON_ONLY = /^ECH_[12]CTA$/i;
const M2_REPEATABLE = /^ECH_(Featured[12]CTA|FeaturedDivider|SectionSpacer\d+|DividerLine|Divider|BlueHeader|TableHeader)$/i;
/** a block that is only a button row — never offered, never duplicated */
export function m2IsButtonBlock(name: string): boolean { return M2_BUTTON_ONLY.test(String(name || "")); }
/** the family a block counts under for "once per email" (1 vs 2 buttons, the hero looks …) */
function m2OnceKey(name: string): string {
  const n = String(name || "");
  const f = M2_FAMILIES.find((x) => x.members.some(([m]) => m.toLowerCase() === n.toLowerCase()));
  return f ? "f:" + f.id : "n:" + n.toLowerCase();
}
export const M2_BUTTON_MSG = "Templates keep the buttons they ship with.";
/** why this block can't be added to an email that already holds `docNames`, or null */
export function m2OnceViolation(docNames: string[], name: string, label?: string): string | null {
  const n = String(name || "");
  if (!n) return null;
  if (m2IsButtonBlock(n)) return M2_BUTTON_MSG;
  if (M2_REPEATABLE.test(n)) return null;
  const key = m2OnceKey(n);
  if (!(docNames || []).some((d) => m2OnceKey(d) === key)) return null;
  return "This email already has " + (label || nameWords(n)) + ". Each module can be added once.";
}

/** the picture-grid groups for a family's looks: [stage, [[name, label]…]] in stage order;
    a look with no known stage lands in "Other" at the end */
export function m2LookGroups(opts: [string, string][], declared?: (name: string) => string): [string, [string, string][]][] {
  const out = new Map<string, [string, string][]>();
  const stageOf = (n: string) => {
    const d = declared ? String(declared(n) || "").trim() : "";
    return d || M2_HERO_STAGE[n] || "Other";
  };
  for (const o of opts) {
    const s = stageOf(o[0]);
    if (!out.has(s)) out.set(s, []);
    out.get(s)!.push(o);
  }
  const order = [...M2_STAGES, "Other"] as string[];
  return Array.from(out.entries()).sort((a, b) => {
    const ia = order.indexOf(a[0]), ib = order.indexOf(b[0]);
    return (ia < 0 ? 98 : ia) - (ib < 0 ? 98 : ib);
  });
}

/** the family an SFMC block belongs to, or null */
export function m2FamilyOf(name: string): M2Family | null {
  const n = String(name || "");
  return M2_FAMILIES.find((f) => f.members.some(([m]) => m === n)) || null;
}
/**
 * The right-panel strip for a family member, in ECH_SWAP's shape so the Inspector draws
 * it with the SAME control. `carrySame` = a field with the same key on both blocks keeps
 * what was typed (the two Main Image sizes share their keys); the rest take the sibling's
 * own defaults, exactly as ECH_SWAP does. Only members actually registered are offered.
 */
export function m2SwapFor(name: string, registered?: (n: string) => boolean): { group: string; opts: [string, string][]; map: Record<string, Record<string, string>>; carrySame: true } | null {
  const f = m2FamilyOf(name);
  if (!f) return null;
  const opts = f.members.filter(([m]) => m === name || !registered || registered(m));
  if (opts.length < 2) return null;
  return { group: f.id, opts, map: f.map || {}, carrySame: true };
}

/**
 * The left panel's groups for the registered library, filtered by `q`.
 * Within a group the order is alphabetical by label, so the panel reads the same on
 * every load whatever order the library arrives in.
 */
export function paletteGroups(metas: PalMeta[], q = ""): PalGroup[] {
  const needle = String(q || "").trim().toLowerCase();
  const out: PalGroup[] = GROUPS.map((g) => ({ id: g.id, title: g.title, items: [] }));
  const other: PalGroup = { id: "other", title: "Other", items: [] };
  /* ── A BLOCK CAN NAME ITS OWN GROUP (Layne 10-29: "could it sit in a new Promo banners
     group?") ── `panelGroup` in the block's guardrails wins over the name rules. A title
     that matches an existing group joins it; a new one becomes its own group at the TOP
     of the panel (approved mockup goty2-promo-20261029). No panelGroup = today's rules. */
  const custom: PalGroup[] = [];
  const groupFor = (title: string): PalGroup => {
    const t = title.toLowerCase();
    const hit = out.find((g) => g.title.toLowerCase() === t) || custom.find((g) => g.title.toLowerCase() === t);
    if (hit) return hit;
    const g: PalGroup = { id: "pg:" + t.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""), title, items: [] };
    custom.push(g);
    return g;
  };
  const seen = new Set<string>();
  for (const m of metas || []) {
    const name = String(m.echName || "");
    if (!m.key || !name || NOT_A_BLOCK.test(name) || seen.has(m.key)) continue;
    seen.add(m.key);
    const label = String((m.gr && m.gr.label) || (m.full && m.full.label) || nameWords(name));
    if (RETIRED_LABEL.test(label)) continue;
    const fl = (m2FamilyOf(name) || { label: "" }).label.toLowerCase();
    if (needle && label.toLowerCase().indexOf(needle) < 0 && name.toLowerCase().indexOf(needle) < 0 && fl.indexOf(needle) < 0) continue;
    const item: PalItem = { type: m.key, name, label, locked: !!m.locked };
    const pg = String((m.full && m.full.panelGroup) || "").trim().slice(0, 40);
    const gi = GROUPS.findIndex((g) => g.re.test(name));
    (pg ? groupFor(pg) : gi >= 0 ? out[gi] : other).items.push(item);
  }
  /* ── ONE CHIP PER FAMILY: the default member stands for the family, under the family's
     label; if the default is missing, the first member that IS registered stands in */
  const famHit: Record<string, PalItem> = {};
  [...custom, ...out, other].forEach((g) => {
    g.items = g.items.filter((i) => {
      const f = m2FamilyOf(i.name);
      if (!f) return true;
      const cur = famHit[f.id];
      const rank = (n: string) => f.members.findIndex(([m]) => m === n);
      if (!cur) {
        famHit[f.id] = i;
        i.family = f.id; i.label = f.label; i.members = f.members.map(([m]) => m);
        return true;
      }
      if (rank(i.name) < rank(cur.name)) { cur.type = i.type; cur.name = i.name; cur.locked = i.locked; }
      return false;
    });
  });
  /* five banners all declare "Hero Headline Image" — a label two chips share gets the
     block's own name after it, so the panel never shows two identical chips */
  const all = [...custom, ...out, other];
  const count: Record<string, number> = {};
  all.forEach((g) => g.items.forEach((i) => { count[i.label] = (count[i.label] || 0) + 1; }));
  all.forEach((g) => g.items.forEach((i) => {
    if (count[i.label] > 1) i.label = i.label + " · " + nameWords(i.name).replace(/\s*Image$/i, "");
  }));
  return all
    .map((g) => ({ ...g, items: g.items.sort((a, b) => a.label.localeCompare(b.label)) }))
    .filter((g) => g.items.length > 0);
}


/* ── PALETTE ICONS (monolith IC, index.html L8096–8115) ─────────────────────────────
   The monolith drew one small 34×26 glyph per block kind; ECH blocks all fell back to
   the body-copy glyph. Here each ECH block gets the glyph of the kind it actually is,
   picked off its SFMC name — the SVGs themselves are the monolith's, verbatim. */
export type IconKey = "logo" | "img" | "headline" | "body" | "btn" | "event" | "card" | "table" | "grid" | "map" | "person" | "divider" | "footer";
const ICON_RULES: [RegExp, IconKey][] = [
  [/^ECH_Logo$/i, "logo"],
  [/^ECH_SocialIcons/i, "footer"],
  [/^ECH_ContactUs/i, "person"],
  [/^ECH_Disclaimer/i, "body"],
  [/^ECH_(SectionSpacer|DividerLine)/i, "divider"],
  [/^ECH_GetDirections/i, "map"],
  [/^ECH_\d?Amenities/i, "grid"],
  [/^ECH_ListingTable|Listing Table/i, "table"],
  [/^ECH_Featured/i, "card"],
  [/^ECH_(TableHeader|CommunitySubHead|BlueHeader)/i, "headline"],
  [/^ECH_GO/i, "event"],
  [/^ECH_\dCTA/i, "btn"],
  [/^ECH_CopyBlock/i, "body"],
  [/Image\d*$/i, "img"],
];
export function iconKey(name: string): IconKey {
  const n = String(name || "");
  for (const [re, k] of ICON_RULES) if (re.test(n)) return k;
  return "body";
}

/* ── THE TABS (monolith .eskind row, index.html L17567) ───────────────────────────── */
export const PAL_TABS: { id: string; label: string }[] = [
  { id: "all", label: "All" },
  { id: "top", label: "Header" },
  { id: "copy", label: "Copy" },
  { id: "homes", label: "Homes" },
  { id: "community", label: "Community" },
  { id: "space", label: "Spacing" },
  { id: "footer", label: "Footer" },
  { id: "__tpl", label: "Template" },
];

/**
 * What one tab shows. `all` = every group; a group id = that group alone; `__tpl` = the
 * blocks of the open template IN TEMPLATE ORDER under one "<template> — in order" head
 * (monolith esPal `kind==="__tpl"`, L12974–12985), so a gap in the set is visible.
 * `tplOrder` is the template's block names in order, `tplName` its display name.
 */
export function paletteView(groups: PalGroup[], tab: string, tplOrder: string[], tplName: string): PalGroup[] {
  if (tab === "__tpl") {
    const byName: Record<string, PalItem> = {};
    groups.forEach((g) => g.items.forEach((i) => { (i.members || [i.name]).forEach((n) => { byName[n] = i; }); }));
    const seen = new Set<string>();
    const items: PalItem[] = [];
    (tplOrder || []).forEach((n) => { const it = byName[n]; if (it && !seen.has(it.type)) { seen.add(it.type); items.push(it); } });
    return items.length ? [{ id: "__tpl", title: (tplName || "Template") + " \u2014 in order", items }] : [];
  }
  if (!tab || tab === "all") return groups;
  return groups.filter((g) => g.id === tab);
}

/* ── GUARDRAILS OFF: THE MOVES ────────────────────────────────────────────────────── */

/**
 * Move `fromId` so it sits at insertion index `to` of the document WITHOUT it — the same
 * index contract `reorder` uses, so the Studio swaps one call for the other. No rule is
 * consulted. Out-of-range indexes clamp; an unknown id returns the document unchanged.
 */
export function freeMove<T extends { id: string }>(doc: T[] | null | undefined, fromId: string, to: number): T[] {
  const d = (doc || []).slice();
  const i = d.findIndex((b) => b.id === fromId);
  if (i < 0) return d;
  const [b] = d.splice(i, 1);
  const at = Math.max(0, Math.min(d.length, to));
  d.splice(at, 0, b);
  return d;
}
